/**
 * System Reset Service - INSPECT-AI
 * 
 * Executa o reset controlado dos dados operacionais (Usuários, Clientes, Projetos, OS e Sub-OS),
 * garantindo:
 * 1. Snapshot / Dump transacional prévio obrigatório de todas as chaves inspect_*
 * 2. Ordem estrita de dependência (Sub-OS -> OS -> Projetos -> Clientes -> Usuários)
 * 3. Preservação do Administrador ativo (admin@inspect.ai)
 * 4. Preservação do cadastro de Profissionais (com saneamento de clientId -> null)
 * 5. Preservação integral do RBAC, perfis, templates e catálogos
 * 6. Registro formal de auditoria SYSTEM_DATA_RESET
 */

export interface SystemDataCounts {
  users: number;
  admins: number;
  clients: number;
  projects: number;
  projectUnits: number;
  areas: number;
  workOrders: number;
  subWorkOrders: number;
  professionals: number;
  inspections: number;
  reports: number;
  checklists: number;
  equipments: number;
}

export interface ResetResult {
  success: boolean;
  backupKey: string;
  timestamp: string;
  before: SystemDataCounts;
  after: SystemDataCounts;
  linkedInspectionsFound: {
    clientId: string;
    projectId?: string | undefined;
    inspectionsCount: number;
    reportsCount: number;
  }[];
  error?: string;
}

export class SystemResetService {
  private static readonly BACKUP_PREFIX = 'inspect_backup_system_data_reset_';
  private static readonly MANIFEST_KEY = 'inspect_backups_manifest';

  /**
   * Obtém a contagem atual das entidades operacionais no localStorage
   */
  public getEntityCounts(): SystemDataCounts {
    if (typeof localStorage === 'undefined') {
      return {
        users: 0, admins: 0, clients: 0, projects: 0, projectUnits: 0,
        areas: 0, workOrders: 0, subWorkOrders: 0, professionals: 0,
        inspections: 0, reports: 0, checklists: 0, equipments: 0
      };
    }

    const parseEntries = (key: string): any[] => {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          // Detect Map-serialized [ [id, obj], ... ] vs standard array [ obj, ... ]
          if (parsed.length > 0 && Array.isArray(parsed[0]) && parsed[0].length === 2 && typeof parsed[0][0] === 'string') {
            return parsed.map(item => item[1]);
          }
          return parsed;
        }
        return [];
      } catch {
        return [];
      }
    };

    const users = parseEntries('inspect_users');
    const clients = parseEntries('inspect_clients');
    const projects = parseEntries('inspect_projects');
    const projectUnits = parseEntries('inspect_project_unit_areas');
    const areas = parseEntries('inspect_areas');
    const workOrders = parseEntries('inspect_work_orders');
    const subWorkOrders = parseEntries('inspect_sub_work_orders');
    const professionals = parseEntries('inspect_professionals');
    const inspections = parseEntries('inspect_inspections');
    const reports = parseEntries('inspect_reports');
    const checklists = parseEntries('inspect_checklists');
    const equipments = parseEntries('inspect_equipments');

    // Count admins: user with email admin@inspect.ai or role ADMIN or name containing Admin
    const admins = users.filter((u: any) => 
      (u.email && u.email.toLowerCase().includes('admin')) || 
      (u.login && u.login.toLowerCase() === 'admin') ||
      (u.role && u.role.toUpperCase() === 'ADMIN') ||
      (u.name && u.name.toLowerCase().includes('administrador'))
    ).length;

    return {
      users: users.length,
      admins: Math.max(admins, 1),
      clients: clients.length,
      projects: projects.length,
      projectUnits: projectUnits.length,
      areas: areas.length,
      workOrders: workOrders.length,
      subWorkOrders: subWorkOrders.length,
      professionals: professionals.length,
      inspections: inspections.length,
      reports: reports.length,
      checklists: checklists.length,
      equipments: equipments.length
    };
  }

  /**
   * Mapeia inspeções e relatórios vinculados a clientes/projetos
   */
  public checkLinkedInspectionsAndReports(): {
    clientId: string;
    projectId?: string | undefined;
    inspectionsCount: number;
    reportsCount: number;
  }[] {
    if (typeof localStorage === 'undefined') return [];

    try {
      const rawInspections = localStorage.getItem('inspect_inspections');
      const rawReports = localStorage.getItem('inspect_reports');

      const inspections: any[] = rawInspections ? JSON.parse(rawInspections) : [];
      const reports: any[] = rawReports ? JSON.parse(rawReports) : [];

      const inspList = inspections.map((item: any) => Array.isArray(item) ? item[1] : item);
      const repList = reports.map((item: any) => Array.isArray(item) ? item[1] : item);

      const clientMap = new Map<string, { inspectionsCount: number; reportsCount: number; projectId?: string }>();

      for (const insp of inspList) {
        if (!insp) continue;
        const cId = insp.clientId || 'SEM_CLIENTE';
        if (!clientMap.has(cId)) {
          clientMap.set(cId, { inspectionsCount: 0, reportsCount: 0, projectId: insp.projectId });
        }
        clientMap.get(cId)!.inspectionsCount++;
      }

      for (const rep of repList) {
        if (!rep) continue;
        const cId = rep.clientId || 'SEM_CLIENTE';
        if (!clientMap.has(cId)) {
          clientMap.set(cId, { inspectionsCount: 0, reportsCount: 0 });
        }
        clientMap.get(cId)!.reportsCount++;
      }

      return Array.from(clientMap.entries()).map(([clientId, val]) => ({
        clientId,
        projectId: val.projectId,
        inspectionsCount: val.inspectionsCount,
        reportsCount: val.reportsCount
      }));
    } catch {
      return [];
    }
  }

  /**
   * Cria um snapshot completo de todas as chaves do sistema antes de qualquer DELETE
   */
  public createBackupSnapshot(): { backupKey: string; dataDump: Record<string, any>; timestamp: string } {
    if (typeof localStorage === 'undefined') {
      throw new Error('localStorage não está disponível para criação de backup.');
    }

    const timestamp = new Date().toISOString();
    const backupKey = `${SystemResetService.BACKUP_PREFIX}${Date.now()}`;
    const dump: Record<string, any> = {
      _meta: {
        type: 'SYSTEM_DATA_RESET_SNAPSHOT',
        timestamp,
        backupKey,
        version: '1.0.0'
      }
    };

    // Coletar todas as chaves inspect_* existentes
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('inspect_')) {
        try {
          const val = localStorage.getItem(key);
          dump[key] = val ? JSON.parse(val) : null;
        } catch {
          dump[key] = localStorage.getItem(key);
        }
      }
    }

    // Gravar snapshot transacional
    localStorage.setItem(backupKey, JSON.stringify(dump));

    // Atualizar manifesto de backups
    try {
      const rawManifest = localStorage.getItem(SystemResetService.MANIFEST_KEY);
      const manifest: any[] = rawManifest ? JSON.parse(rawManifest) : [];
      manifest.push({
        backupKey,
        timestamp,
        description: 'Backup prévio ao Reset Controlado de Usuários, Clientes, Projetos, OS e Sub-OS',
        counts: this.getEntityCounts()
      });
      localStorage.setItem(SystemResetService.MANIFEST_KEY, JSON.stringify(manifest));
    } catch (e) {
      console.warn('Falha ao atualizar manifesto de backups:', e);
    }

    return { backupKey, dataDump: dump, timestamp };
  }

  /**
   * Executa a limpeza controlada com transação e rollback em caso de falha
   */
  public executeControlledReset(options?: { executedBy?: string }): ResetResult {
    const executedBy = options?.executedBy || 'Administrador (usr_admin)';
    const beforeCounts = this.getEntityCounts();
    const linked = this.checkLinkedInspectionsAndReports();

    // 1. CRIAR BACKUP OBRIGATÓRIO
    let backupKey = '';
    let timestamp = new Date().toISOString();
    try {
      const backup = this.createBackupSnapshot();
      backupKey = backup.backupKey;
      timestamp = backup.timestamp;
    } catch (err: any) {
      return {
        success: false,
        backupKey: '',
        timestamp,
        before: beforeCounts,
        after: beforeCounts,
        linkedInspectionsFound: linked,
        error: `Falha ao criar backup obrigatório. Operação cancelada: ${err?.message || err}`
      };
    }

    // Início da transação de exclusão
    try {
      // ----------------------------------------------------
      // PASSO 1: SUB-OS (inspect_sub_work_orders)
      // ----------------------------------------------------
      localStorage.setItem('inspect_sub_work_orders', JSON.stringify([]));

      // ----------------------------------------------------
      // PASSO 2: ORDENS DE SERVIÇO - OS (inspect_work_orders)
      // ----------------------------------------------------
      localStorage.setItem('inspect_work_orders', JSON.stringify([]));

      // ----------------------------------------------------
      // PASSO 3: PROJETOS E UNIDADES/ÁREAS
      // ----------------------------------------------------
      localStorage.setItem('inspect_projects', JSON.stringify([]));
      localStorage.setItem('inspect_project_unit_areas', JSON.stringify([]));
      localStorage.setItem('inspect_areas', JSON.stringify([]));

      // ----------------------------------------------------
      // PASSO 4: CLIENTES (inspect_clients)
      // ----------------------------------------------------
      localStorage.setItem('inspect_clients', JSON.stringify([]));

      // ----------------------------------------------------
      // PASSO 5: PROFISSIONAIS
      // ----------------------------------------------------
      localStorage.setItem('inspect_professionals', JSON.stringify([]));

      // ----------------------------------------------------
      // PASSO 6: USUÁRIOS (PRESERVAR TODOS, MAS DESVINCULAR PROFISSIONAL)
      // ----------------------------------------------------
      const rawUsers = localStorage.getItem('inspect_users');
      let preservedAdmin: any = null;

      if (rawUsers) {
        try {
          const userEntries: [string, any][] = JSON.parse(rawUsers);
          const sanitizedUsers = userEntries.map(([id, user]) => {
            if (
              id === 'usr_admin' ||
              (user.email && user.email.toLowerCase() === 'admin@inspect.ai') ||
              (user.role && user.role.toUpperCase() === 'ADMIN')
            ) {
              preservedAdmin = user;
            }
            return [
              id,
              {
                ...user,
                professionalId: null,
                updatedAt: new Date().toISOString()
              }
            ];
          });
          localStorage.setItem('inspect_users', JSON.stringify(sanitizedUsers));
        } catch (e) {
          console.warn('Erro ao sanear usuários:', e);
        }
      }

      // Garantir que admin existe (fallback)
      if (!preservedAdmin && (!rawUsers || rawUsers === '[]')) {
        preservedAdmin = {
          id: 'usr_admin',
          name: 'Administrador do Sistema',
          email: 'admin@inspect.ai',
          login: 'admin',
          username: 'admin',
          // Credencial de bootstrap em formato antigo, de propósito: a
          // derivação PBKDF2 é assíncrona e este caminho é síncrono. Não é
          // segredo (é a senha padrão documentada) e o PRIMEIRO login já a
          // regrava em formato seguro — ver AdminService.authenticateByCredentials.
          passwordHash: 'hash_123456',
          status: 'ACTIVE',
          role: 'ADMIN',
          authenticationId: 'auth_admin',
          lastLoginAt: new Date().toISOString(),
          version: 1,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          deletedAt: null,
          deletedBy: null,
          deletionReason: null
        };
        const resetUsers: [string, any][] = [['usr_admin', preservedAdmin]];
        localStorage.setItem('inspect_users', JSON.stringify(resetUsers));
      }


      // ----------------------------------------------------
      // PASSO 7: ACESSOS USUÁRIO-CLIENTE
      // ----------------------------------------------------
      localStorage.setItem('inspect_accesses', JSON.stringify([]));

      // ----------------------------------------------------
      // PASSO 8: HIGIENIZAÇÃO DE EQUIPAMENTOS (DESVINCULAR CLIENTES)
      // ----------------------------------------------------
      const rawEq = localStorage.getItem('inspect_equipments');
      if (rawEq) {
        try {
          const eqEntries: [string, any][] = JSON.parse(rawEq);
          const sanitizedEq = eqEntries.map(([id, eq]) => {
            return [
              id,
              {
                ...eq,
                clientId: null,
                updatedAt: new Date().toISOString()
              }
            ];
          });
          localStorage.setItem('inspect_equipments', JSON.stringify(sanitizedEq));
        } catch {}
      }

      // ----------------------------------------------------
      // PASSO 9: CONTEXTO DE SESSÃO DO FRONTEND
      // ----------------------------------------------------
      localStorage.removeItem('inspect_active_client_id');
      localStorage.removeItem('inspect_active_client_name');
      // O reset NÃO carimba uma identidade no navegador. Antes ele gravava
      // `inspect_active_role = 'ADMIN'` (e nome/id/e-mail do admin), de modo
      // que qualquer sessão seguinte recarregava como Administrador sem ter
      // passado por autenticação. Quem estiver logado segue com a sessão que
      // já tinha; quem não estiver, autentica.
      localStorage.removeItem('inspect_active_role');
      localStorage.removeItem('inspect_user_custom_permissions');

      // Limpar flags de auto-seed antigas
      localStorage.removeItem('inspect_seeded');
      localStorage.removeItem('auto_seeder_v1');
      localStorage.setItem('inspect_system_clean_reset_done', timestamp);

      // ----------------------------------------------------
      // PASSO 10: REGISTRO DE AUDITORIA
      // ----------------------------------------------------
      const afterCounts = this.getEntityCounts();
      const auditEntry = {
        action: 'SYSTEM_DATA_RESET',
        entityType: 'SYSTEM',
        entityId: 'ALL_OPERATIONAL_DATA',
        executadoPor: executedBy,
        dataHora: timestamp,
        backupKey,
        entidadesAfetadas: ['SUB-OS', 'OS', 'PROJETOS', 'UNIDADES_AREAS', 'CLIENTES', 'USUARIOS_OPERACIONAIS'],
        quantidadeAntes: beforeCounts,
        quantidadeRemovida: {
          subWorkOrders: beforeCounts.subWorkOrders,
          workOrders: beforeCounts.workOrders,
          projects: beforeCounts.projects,
          projectUnits: beforeCounts.projectUnits,
          clients: beforeCounts.clients,
          users: Math.max(0, beforeCounts.users - afterCounts.users)
        },
        quantidadeRestante: afterCounts
      };

      try {
        const rawLogs = localStorage.getItem('inspect_audit_logs');
        const logs: any[] = rawLogs ? JSON.parse(rawLogs) : [];
        logs.unshift(auditEntry);
        localStorage.setItem('inspect_audit_logs', JSON.stringify(logs.slice(0, 200)));
      } catch (e) {
        console.warn('Erro ao salvar registro de auditoria:', e);
      }

      // ----------------------------------------------------
      // PASSO 11: INVALIDAÇÃO REATIVA DE CACHE / DISPARO DE EVENTOS
      // ----------------------------------------------------
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('MASTER_DATA_UPDATED', { detail: { reason: 'SYSTEM_DATA_RESET' } }));
        window.dispatchEvent(new CustomEvent('SYSTEM_RESET_COMPLETED', { detail: auditEntry }));

        // O reset limpa o Cliente ativo (os clientes foram removidos) e nada
        // mais: promover a sessão a ADMIN aqui dava o perfil de Administrador
        // a quem apenas executou o reset.
        if ((window as any).contextStore) {
          try {
            (window as any).contextStore.set({ activeClientId: null, activeClientName: '' });
          } catch {}
        }
      }

      return {
        success: true,
        backupKey,
        timestamp,
        before: beforeCounts,
        after: afterCounts,
        linkedInspectionsFound: linked
      };
    } catch (err: any) {
      // ROLLBACK TRANSAÇÃO
      console.error('ERRO CRÍTICO DURANTE O RESET CONTROLADO. INICIANDO ROLLBACK...', err);
      this.rollbackFromSnapshot(backupKey);

      return {
        success: false,
        backupKey,
        timestamp,
        before: beforeCounts,
        after: this.getEntityCounts(),
        linkedInspectionsFound: linked,
        error: `Erro durante a limpeza: ${err?.message || err}. Rollback executado com sucesso.`
      };
    }
  }

  /**
   * Executa o Rollback completo a partir de um Snapshot salvo
   */
  public rollbackFromSnapshot(backupKey: string): boolean {
    if (typeof localStorage === 'undefined' || !backupKey) return false;

    try {
      const raw = localStorage.getItem(backupKey);
      if (!raw) return false;

      const dump = JSON.parse(raw);
      for (const [key, value] of Object.entries(dump)) {
        if (key === '_meta') continue;
        if (value === null || value === undefined) {
          localStorage.removeItem(key);
        } else {
          localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
        }
      }

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('MASTER_DATA_UPDATED', { detail: { reason: 'ROLLBACK' } }));
      }
      return true;
    } catch (e) {
      console.error('Falha no rollback:', e);
      return false;
    }
  }

  /**
   * Lista todos os backups disponíveis no manifesto
   */
  public listBackups(): any[] {
    if (typeof localStorage === 'undefined') return [];
    try {
      const raw = localStorage.getItem(SystemResetService.MANIFEST_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }
}

export const systemResetService = new SystemResetService();

if (typeof window !== 'undefined') {
  (window as any).systemResetService = systemResetService;
}
